import { 
    SOL_PRICE_USD, 
    TOTAL_AUCTION_TOKENS,
    START_PRICE,
    CIRCULATING_SUPPLY
} from './constants';
import priceService from '../services/priceService';

class PriceCalculator {
    constructor() {
        this.totalSolRaised = 0;
        this.currentPrice = 0;
        this.dexPrice = 0;
        this.moonshot = 0;
        this.marketCap = 0;
        this.realTimeSolPrice = SOL_PRICE_USD;
        
        // Fetch real-time SOL price on initialization
        this.fetchRealTimeSolPrice();
        // No need for interval here - priceService now handles this centrally
    }
    
    // Fetch real-time SOL price from CoinGecko
    async fetchRealTimeSolPrice() {
        try {
            const price = await priceService.getSolanaPrice();
            if (price) {
                this.realTimeSolPrice = price;
                
                // Recalculate with new price if we have data
                if (this.totalSolRaised > 0) {
                    this.calculate(this.totalSolRaised);
                }
            }
        } catch (error) {
            console.error('Failed to fetch real-time SOL price:', error);
        }
    }

    // Update calculations with new SOL amount
    calculate(totalSolRaised) {
        this.totalSolRaised = totalSolRaised;
        
        // Use real-time SOL price instead of constant
        const solPrice = this.realTimeSolPrice || SOL_PRICE_USD;
        console.log('Using SOL price for calculations:', solPrice);
        
        // Calculate USD value of raised SOL
        const totalValueInUSD = totalSolRaised * solPrice;
        
        // Calculate current price (auction price)
        // This is the price during the auction based on contributions
        this.currentPrice = totalValueInUSD / TOTAL_AUCTION_TOKENS;
        
        // Calculate DEX price (estimated price after listing)
        // 90% of SOL goes to liquidity, paired with 37.5M JINU
        const liquiditySOL = totalSolRaised * 0.9;
        const liquidityJINU = TOTAL_AUCTION_TOKENS; // 37.5M JINU for liquidity
        this.dexPrice = (liquiditySOL * solPrice) / liquidityJINU;
        
        // Calculate moonshot multiplier (potential growth from zero/near-zero)
        // Using a very small starting price to represent "near zero" for calculation
        const effectiveStartPrice = 0.00001; // $0.00001 as effective starting price
        this.moonshot = Math.floor(this.dexPrice / effectiveStartPrice);
        
        // Calculate market cap (circulating supply * current price)
        this.marketCap = CIRCULATING_SUPPLY * this.currentPrice;

        return {
            totalSolRaised: this.totalSolRaised,
            currentPrice: this.currentPrice,
            dexPrice: this.dexPrice,
            moonshot: this.moonshot,
            marketCap: this.marketCap,
            startPrice: START_PRICE,
            circulatingSupply: CIRCULATING_SUPPLY,
            solPrice: solPrice
        };
    }

    // Calculate personal entry price based on current auction state
    calculatePrice(jinuAllocation) {
        // If no allocation, return the current price
        if (!jinuAllocation || jinuAllocation <= 0) {
            return this.currentPrice || 0.0136; // Default to current calculated price
        }
        
        // Use the current price as the entry price
        // This represents the price at the time of contribution
        return this.currentPrice || 0.0136;
    }
    
    // Calculate personal moonshot multiplier
    calculatePersonalMoonshot(entryPrice, targetPrice = 1) {
        if (!entryPrice || entryPrice <= 0) return 0;
        return Math.floor(targetPrice / entryPrice);
    }

    // Format price for display
    formatPrice(price) {
        if (price === undefined || price === null) return "$0";
        
        // For very small prices, show more decimal places
        if (price < 0.01) {
            return `$${price.toFixed(6)}`;
        } else if (price < 0.1) {
            return `$${price.toFixed(4)}`;
        } else if (price < 1) {
            return `$${price.toFixed(3)}`;
        } else if (price < 1000) {
            return `$${price.toFixed(2)}`;
        } else if (price < 1000000) {
            return `$${(price / 1000).toFixed(2)}K`;
        } else {
            return `$${(price / 1000000).toFixed(2)}M`;
        }
    }

    formatNumber(num) {
        if (!num) return '0';
        
        // Format large numbers with K, M, B suffixes
        if (num >= 1000000000) {
            return `${(num / 1000000000).toFixed(1)}B`;
        } else if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1)}M`;
        } else if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K`;
        }
        
        return num.toLocaleString();
    }

    formatMoonshot(moonshot) {
        return `${moonshot}x`;
    }

    formatMarketCap(marketCap) {
        return `$${this.formatNumber(marketCap)}`;
    }
}

// Create a singleton instance
const priceCalculator = new PriceCalculator();
export default priceCalculator;
